import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type { Piece } from '@miraki/contracts';
import { api } from '../../api/client';
import { useSession } from '../../auth/session';
import { Alert, Chip, Empty, Money, Note, PageHeader, Panel, Spinner, Table, type Tone } from '../../ui';
import { bestSellers, bySaleDate, notYet, partition, paymentStateVisible, type NotYet } from './consignment-read';

/**
 * R-21 Sold Items — SL-06…SL-08, and the history a retailer reorders from (D25).
 *
 * Days to sale is `daysHeld` — the ONE definition (D80), which ends at the sale for a sold piece.
 * It is shown and averaged here; it is never recomputed from dates.
 *
 * Whether a sold piece is invoiced or paid is DEBT detail. DB-07 keeps invoice and payment state
 * from a user without `shop.ledger` (a Buyer), so for them every sold piece reads simply "Sold".
 *
 * Reorder (SL-07) goes to the catalogue, not straight into the cart: `zPiece` carries no variant
 * code, so there is nothing to add. Hand-over asks for one on the contract. The link is shown only
 * to someone who may order (`shop.order`) — DB-07 gives Accounts no ordering path.
 */

const TONE: Record<'SOLD' | 'INVOICED' | 'PAID', Tone> = { SOLD: 'run', INVOICED: 'them', PAID: 'done' };

async function readOrNotYet(): Promise<{ data: Piece[] } | { notYet: NotYet }> {
  try {
    return { data: await api.get<Piece[]>('/shop/sold') };
  } catch (err) {
    const n = notYet(err);
    if (n) return { notYet: n };
    throw err;
  }
}

export function SoldItems() {
  const { t } = useTranslation();
  const { me } = useSession();
  const permissions = me?.kind === 'retailer' ? me.permissions : [];
  const showPayment = paymentStateVisible(permissions);
  const mayOrder = permissions.includes('shop.order');
  const { data, isLoading, error } = useQuery({ queryKey: ['shop', 'sold'], queryFn: readOrNotYet });

  if (isLoading) return <Spinner />;
  if (error) return <Alert tone="stop">{error.message}</Alert>;
  if (!data) return null;

  if ('notYet' in data) {
    return (
      <>
        <PageHeader title={t('soldItems.title')} />
        <Note>{t('soldItems.notYet')}{data.notYet.waitingFor ? <span className="mk-dim"> ({data.notYet.waitingFor})</span> : null}</Note>
      </>
    );
  }

  // Only sold, invoiced and paid pieces belong here, whatever the route returns.
  const sold = partition(data.data).SOLD.sort(bySaleDate);
  const best = bestSellers(sold);

  return (
    <>
      <PageHeader title={t('soldItems.title')} />

      {/* Same shared cause as My Inventory's: `mk-grid2` reserved half the page for a panel that
          is only sometimes rendered, so a five-column history sat in half the width. */}
      <div className={`mk-listdetail${best.length ? ' on' : ''}`}>
        <Panel title={t('soldItems.history')}>
          <Table
            rows={sold}
            rowKey={(p) => p.id}
            columns={[
              { header: t('soldItems.colPiece'), render: (p) => <>{p.description}<div className="mk-hint">{p.jobNumber}</div></> },
              { header: t('soldItems.colColour'), render: (p) => <span className="mk-dim">{p.goldColour ?? '—'}</span> },
              { header: t('soldItems.colSoldOn'), render: (p) => p.soldOn ?? '—' },
              { header: t('soldItems.colDays'), numeric: true, render: (p) => p.daysHeld ?? '—' },
              { header: t('soldItems.colWholesale'), numeric: true, render: (p) => <Money amount={p.wholesaleValue} currency={p.currency} /> },
              {
                header: '',
                render: (p) => showPayment
                  ? <Chip tone={TONE[p.status as keyof typeof TONE]}>{t(`soldItems.state.${p.status}`)}</Chip>
                  : <Chip tone="run">{t('soldItems.state.SOLD')}</Chip>,
              },
              ...(mayOrder ? [{ header: '', width: '1%', render: () => <Link to="/shop/catalog">{t('soldItems.reorder')}</Link> }] : []),
            ]}
            empty={<Empty title={t('soldItems.emptyTitle')} body={t('soldItems.emptyBody')} />}
          />
        </Panel>

        {best.length ? (
          <Panel title={t('soldItems.best')}>
            <Table
              rows={best}
              rowKey={(b) => b.name}
              columns={[
                { header: t('soldItems.colProduct'), render: (b) => b.name },
                { header: t('soldItems.colCount'), numeric: true, render: (b) => b.sold },
                { header: t('soldItems.colAvgDays'), numeric: true, render: (b) => b.avgDaysToSale ?? '—' },
              ]}
            />
            <div className="mk-pbody"><Note>{t('soldItems.bestNote')}</Note></div>
          </Panel>
        ) : null}
      </div>
    </>
  );
}
